/**
 * Updates the video input device for the ChatBox object by requesting access to the device
 * specified in the chat.settings.videoIn.value property, and replacing the existing video
 * track with the new track obtained from the new device stream. If the new track cannot be
 * obtained, or if it is not a video track, the function returns without any further actions.
 *
 * @param {ChatBox} chat - The ChatBox object for which to update the video input device.
 * @returns {void}
 */
export function videoInDeviceChanged (chat) {
  chat.cleanupLocalTracks()

  navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: chat.settings.videoIn.value } } })
    .then(async (newStream) => {
      const newTrack = newStream.getTracks().find(sender => sender.kind === 'video')
      const kind = newTrack.kind
      if (kind !== 'video') {
        return
      }

      let sender = chat.session.sessionDescriptionHandler?.peerConnection.getSenders().find(sender => sender?.track?.kind === kind)
      if (sender) {
        newTrack.enabled = chat.videoStreamingActive
        await sender.replaceTrack(newTrack)

        chat.localStream.addTrack(newTrack)
        chat.localVideo.srcObject = chat.localStream
        chat.localVideo.play()
      }
    })
    .catch(error => {
      console.error('Error getting new video input device:', error)
    })
}

/**
 * Updates the audio input device for the ChatBox object by requesting access to the device
 * specified in the chat.settings.audioIn.value property, and replacing the existing audio
 * track with the new track obtained from the new device stream. If the new track cannot be
 * obtained, or if it is not an audio track, the function returns without any further actions.
 *
 * @param {ChatBox} chat - The ChatBox object for which to update the audio input device.
 * @returns {void}
 */
export function audioInDeviceChanged (chat) {
  navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: chat.settings.audioIn.value } } })
    .then(newStream => {
      const newTrack = newStream.getTracks().find(sender => sender.kind === 'audio')
      const kind = newTrack?.kind
      if (kind !== 'audio') {
        return
      }

      chat.session.sessionDescriptionHandler?.peerConnection.getSenders().forEach((sender) => {
        if (sender.track?.kind === kind) {
          sender.track.stop()
        }
      })

      chat.session.sessionDescriptionHandler?.peerConnection.getSenders().find(sender => sender?.track?.kind === kind)?.replaceTrack(newTrack)
    })
    .catch(error => {
      console.error('Error getting new audio input device:', error)
    })
}
